import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiClient } from '../utils/apiClient';
import { API_BASE_URL } from '../constants/api';
import { useAuth } from '../context/AuthContext';
import { hasEffectivePermission } from '../utils/accessControl';
import '../styles/Accounting.css';

const API_BASE = API_BASE_URL;
const ROLE_ADMIN = 1;
const ROLE_ACCOUNTANT = 6;

const now = new Date();
const YEAR_OPTIONS = Array.from({ length: 6 }).map((_, idx) => now.getFullYear() - 2 + idx);
const MONTH_OPTIONS = [
  { value: 1, label: 'Ocak' },
  { value: 2, label: 'Şubat' },
  { value: 3, label: 'Mart' },
  { value: 4, label: 'Nisan' },
  { value: 5, label: 'Mayıs' },
  { value: 6, label: 'Haziran' },
  { value: 7, label: 'Temmuz' },
  { value: 8, label: 'Ağustos' },
  { value: 9, label: 'Eylül' },
  { value: 10, label: 'Ekim' },
  { value: 11, label: 'Kasım' },
  { value: 12, label: 'Aralık' },
];

const formatMoney = (value) =>
  new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const formatPct = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '-';
  return `%${Number(value).toFixed(1)}`;
};

const toInputValue = (value) => (Number(value || 0) === 0 ? '' : String(value));

const parseNumericInput = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const raw = String(value).trim();
  if (!raw) return null;

  let normalized = raw;
  if (normalized.includes(',') && normalized.includes('.')) {
    normalized = normalized.replace(/\./g, '').replace(/,/g, '.');
  } else if (normalized.includes(',')) {
    normalized = normalized.replace(/,/g, '.');
  }

  normalized = normalized.replace(/[^\d.-]/g, '');
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const toNumberOrZero = (value) => {
  const parsed = parseNumericInput(value);
  return parsed === null ? 0 : Number(parsed);
};

const toFixedNumber = (value) => Number(Number(value || 0).toFixed(2));

const getRowLedgerValues = (row) => {
  const debitAmount = toFixedNumber(toNumberOrZero(row.debit_amount_input));
  const creditAmount = toFixedNumber(toNumberOrZero(row.credit_amount_input));
  const netAmount = toFixedNumber(debitAmount - creditAmount);

  return {
    debitAmount,
    creditAmount,
    debitBalance: netAmount > 0 ? netAmount : 0,
    creditBalance: netAmount < 0 ? Math.abs(netAmount) : 0,
  };
};

const Accounting = () => {
  const { user, permissions } = useAuth();
  const roleId = Number(user?.role_id);
  const canAccess = [ROLE_ADMIN, ROLE_ACCOUNTANT].includes(roleId);
  const canManageFinance = hasEffectivePermission(user, permissions, 'can_manage_finance');

  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [rows, setRows] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [procurementOverview, setProcurementOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [expandedGroupMap, setExpandedGroupMap] = useState({});

  const fetchAccountingData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [entriesRes, dashboardRes, procurementRes] = await Promise.all([
        apiClient.get(`${API_BASE}/accounting/entries?year=${year}&month=${month}`),
        apiClient.get(`${API_BASE}/accounting/dashboard?year=${year}&month=${month}`),
        apiClient.get(`${API_BASE}/accounting/procurement-overview?year=${year}&month=${month}`),
      ]);

      const fetchedRows = Array.isArray(entriesRes.data?.entries) ? entriesRes.data.entries : [];
      setRows(
        fetchedRows.map((row) => ({
          ...row,
          debit_amount_input: toInputValue(row.debit_amount),
          credit_amount_input: toInputValue(row.credit_amount),
          note_input: row.note || '',
        }))
      );
      setDashboard(dashboardRes.data || null);
      setProcurementOverview(procurementRes.data || null);
      setDirty(false);
      setIsEditMode(false);
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Muhasebe verileri y?klenemedi.');
      setRows([]);
      setDashboard(null);
      setProcurementOverview(null);
    } finally {
      setLoading(false);
    }
  }, [month, year]);

  useEffect(() => {
    if (!canAccess) return;
    fetchAccountingData();
  }, [canAccess, fetchAccountingData]);

  const groupedRows = useMemo(
    () =>
      rows.reduce((acc, row) => {
        const groupName = row.group_name || 'Diğer';
        if (!acc[groupName]) acc[groupName] = [];
        acc[groupName].push(row);
        return acc;
      }, {}),
    [rows]
  );

  useEffect(() => {
    setExpandedGroupMap((prev) => {
      const next = { ...prev };
      Object.keys(groupedRows).forEach((groupName) => {
        if (typeof next[groupName] !== 'boolean') next[groupName] = true;
      });
      return next;
    });
  }, [groupedRows]);

  const toggleGroup = (groupName) => {
    setExpandedGroupMap((prev) => ({
      ...prev,
      [groupName]: !prev[groupName],
    }));
  };

  const handleFieldChange = (categoryId, key, value) => {
    setRows((prev) =>
      prev.map((row) =>
        Number(row.category_id) === Number(categoryId) ? { ...row, [key]: value } : row
      )
    );
    setDirty(true);
    setSuccess('');
  };

  const handleStartEdit = () => {
    if (!canManageFinance) return;
    setIsEditMode(true);
    setSuccess('');
  };

  const handleCancelEdit = async () => {
    await fetchAccountingData();
    setSuccess('');
  };

  const handleSave = async () => {
    if (!canManageFinance) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        year,
        month,
        entries: rows.map((row) => ({
          category_id: row.category_id,
          code: row.code,
          category_name: row.category_name,
          group_name: row.group_name,
          debit_amount: row.debit_amount_input,
          credit_amount: row.credit_amount_input,
          note: row.note_input,
        })),
      };

      await apiClient.put(`${API_BASE}/accounting/entries`, payload);
      setSuccess('Muhasebe kayıtları kaydedildi.');
      await fetchAccountingData();
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Kayıtlar kaydedilemedi.');
    } finally {
      setSaving(false);
    }
  };

  if (!canAccess) {
    return (
      <div className="accounting-page">
        <div className="accounting-card">
          <h2>Muhasebe Paneli</h2>
          <p>Bu alana erişiminiz yok.</p>
        </div>
      </div>
    );
  }

  const procurementSummary = procurementOverview?.summary || {};
  const missingInvoiceOrders = Array.isArray(procurementOverview?.missing_invoice_orders)
    ? procurementOverview.missing_invoice_orders
    : [];
  const supplierBreakdown = Array.isArray(procurementOverview?.supplier_breakdown)
    ? procurementOverview.supplier_breakdown
    : [];
  const projectBreakdown = Array.isArray(procurementOverview?.project_breakdown)
    ? procurementOverview.project_breakdown
    : [];

  const trialCurrent = dashboard?.trial_balance_totals?.current || {};
  const trialPrev = dashboard?.trial_balance_totals?.prev_month || {};
  const trialNetCurrent = toFixedNumber(
    Number(trialCurrent.debit_amount || 0) - Number(trialCurrent.credit_amount || 0)
  );
  const trialNetPrev = toFixedNumber(
    Number(trialPrev.debit_amount || 0) - Number(trialPrev.credit_amount || 0)
  );

  return (
    <div className="accounting-page">
      <div className="accounting-head">
        <div>
          <h1>Muhasebe Paneli</h1>
          <p>Mizan girişi borç ve alacak ile yapılır, bakiye alanları otomatik hesaplanır.</p>
        </div>
        <div className="accounting-head-actions">
          <label>
            Yıl
            <select value={year} onChange={(event) => setYear(Number(event.target.value))}>
              {YEAR_OPTIONS.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </label>
          <label>
            Ay
            <select value={month} onChange={(event) => setMonth(Number(event.target.value))}>
              {MONTH_OPTIONS.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
          <button type="button" onClick={fetchAccountingData} className="secondary-btn">
            Yenile
          </button>
          {!isEditMode ? (
            <button type="button" onClick={handleStartEdit} className="primary-btn">
              Düzenle
            </button>
          ) : (
            <>
              <button type="button" onClick={handleCancelEdit} className="secondary-btn">
                İptal
              </button>
              <button type="button" onClick={handleSave} disabled={!dirty || saving} className="primary-btn">
                {saving ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </>
          )}
        </div>
      </div>

      {error && <div className="accounting-alert error">{error}</div>}
      {success && <div className="accounting-alert success">{success}</div>}

      {loading ? (
        <div className="accounting-card">Yükleniyor...</div>
      ) : (
        <>
          <div className="accounting-metrics">
            <div className="metric-card">
              <span>Seçili Ay Bakiye Toplamı</span>
              <strong>{formatMoney(dashboard?.totals?.effective_amount || dashboard?.totals?.current_total)}</strong>
            </div>
            <div className="metric-card">
              <span>Toplam Borç</span>
              <strong>{formatMoney(trialCurrent.debit_amount)}</strong>
              <small>Önceki ay: {formatMoney(trialPrev.debit_amount)}</small>
            </div>
            <div className="metric-card">
              <span>Toplam Alacak</span>
              <strong>{formatMoney(trialCurrent.credit_amount)}</strong>
              <small>Önceki ay: {formatMoney(trialPrev.credit_amount)}</small>
            </div>
            <div className="metric-card">
              <span>Borç Bakiyesi Toplamı</span>
              <strong>{formatMoney(trialCurrent.debit_balance)}</strong>
              <small>Önceki ay: {formatMoney(trialPrev.debit_balance)}</small>
            </div>
            <div className="metric-card">
              <span>Alacak Bakiyesi Toplamı</span>
              <strong>{formatMoney(trialCurrent.credit_balance)}</strong>
              <small>Önceki ay: {formatMoney(trialPrev.credit_balance)}</small>
            </div>
            <div className="metric-card">
              <span>Net Fark (Borç - Alacak)</span>
              <strong>{formatMoney(trialNetCurrent)}</strong>
              <small>Önceki ay: {formatMoney(trialNetPrev)}</small>
            </div>
          </div>

          <div className="accounting-grid">
            <section className="accounting-card">
              <h3>Uyarı Merkezi</h3>
              {!dashboard?.alerts?.length ? (
                <p>Şu an kritik bir sapma bulunmuyor.</p>
              ) : (
                <ul className="alert-list">
                  {dashboard.alerts.map((alert) => (
                    <li key={alert.code} className={`alert-item ${alert.level}`}>
                      {alert.message}
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="accounting-card">
              <h3>En Yüksek Artış Gösterilen Kalemler</h3>
              {!dashboard?.top_increases?.length ? (
                <p>Karşılaştırılabilir veri bulunmuyor.</p>
              ) : (
                <div className="increase-list">
                  {dashboard.top_increases.slice(0, 5).map((item) => (
                    <div className="increase-row" key={`${item.code}-${item.name}`}>
                      <div>
                        <strong>{item.code}</strong>
                        <p>{item.name}</p>
                      </div>
                      <div className="increase-values">
                        <span>{formatMoney(item.current_amount)}</span>
                        <small>{formatPct(item.change_pct)}</small>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>


          <div className="accounting-metrics procurement-metrics">
            <div className="metric-card">
              <span>Talep Toplamı</span>
              <strong>{formatMoney(procurementSummary.requested_total)}</strong>
            </div>
            <div className="metric-card">
              <span>Siparişe Dönen</span>
              <strong>{formatMoney(procurementSummary.ordered_total)}</strong>
            </div>
            <div className="metric-card">
              <span>Teslim Alınan</span>
              <strong>{formatMoney(procurementSummary.received_total)}</strong>
            </div>
            <div className="metric-card">
              <span>Faturalaşan</span>
              <strong>{formatMoney(procurementSummary.invoiced_total)}</strong>
            </div>
            <div className="metric-card">
              <span>Ödenen</span>
              <strong>{formatMoney(procurementSummary.paid_total)}</strong>
            </div>
          </div>

          <div className="accounting-grid procurement-grid">
            <section className="accounting-card">
              <h3>Faturası Eksik Teslimler</h3>
              {!missingInvoiceOrders.length ? (
                <p>Bu ay için faturası eksik teslim görünmüyor.</p>
              ) : (
                <div className="increase-list">
                  {missingInvoiceOrders.map((item) => (
                    <div className="increase-row" key={`missing-invoice-${item.id}`}>
                      <div>
                        <strong>{item.resource_name}</strong>
                        <p>{item.project_name} | {item.supplier_name}</p>
                      </div>
                      <div className="increase-values">
                        <span>{formatMoney(item.total_amount)}</span>
                        <small>{item.delivered_date ? new Date(item.delivered_date).toLocaleDateString('tr-TR') : '-'}</small>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="accounting-card">
              <h3>Tedarikçi Bazlı Fatura Yoğunluğu</h3>
              {!supplierBreakdown.length ? (
                <p>Faturalı satın alma kaydı bulunmuyor.</p>
              ) : (
                <div className="increase-list">
                  {supplierBreakdown.map((item) => (
                    <div className="increase-row" key={`supplier-${item.supplier_name}`}>
                      <div>
                        <strong>{item.supplier_name}</strong>
                      </div>
                      <div className="increase-values">
                        <span>{formatMoney(item.amount)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="accounting-card">
              <h3>Proje Bazlı Satın Alma</h3>
              {!projectBreakdown.length ? (
                <p>Proje bazlı satın alma dağılımı henüz oluşmadı.</p>
              ) : (
                <div className="increase-list">
                  {projectBreakdown.map((item) => (
                    <div className="increase-row" key={`project-${item.project_name}`}>
                      <div>
                        <strong>{item.project_name}</strong>
                      </div>
                      <div className="increase-values">
                        <span>{formatMoney(item.amount)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
          <div className="accounting-table-wrap">
            {Object.entries(groupedRows).map(([groupName, groupItems]) => {
              const groupTotals = groupItems.reduce(
                (acc, row) => {
                  const ledgerValues = getRowLedgerValues(row);
                  acc.debitAmount += ledgerValues.debitAmount;
                  acc.creditAmount += ledgerValues.creditAmount;
                  acc.debitBalance += ledgerValues.debitBalance;
                  acc.creditBalance += ledgerValues.creditBalance;
                  return acc;
                },
                { debitAmount: 0, creditAmount: 0, debitBalance: 0, creditBalance: 0 }
              );

              return (
                <section className="accounting-card" key={groupName}>
                  <button type="button" className="accounting-group-header" onClick={() => toggleGroup(groupName)}>
                    <span>{groupName}</span>
                    <span>{expandedGroupMap[groupName] ? '?' : '?'}</span>
                  </button>

                  {expandedGroupMap[groupName] && (
                    <>
                      <div className="accounting-table-scroll">
                        <table className="accounting-table">
                          <thead>
                            <tr>
                              <th className="code-head">Kod</th>
                              <th className="name-head">Hesap Adı</th>
                              <th className="amount-head">Borç</th>
                              <th className="amount-head">Alacak</th>
                              <th className="amount-head">Borç Bakiyesi</th>
                              <th className="amount-head">Alacak Bakiyesi</th>
                              <th className="note-head">Not</th>
                            </tr>
                          </thead>
                          <tbody>
                            {groupItems.map((row) => {
                              const ledgerValues = getRowLedgerValues(row);
                              return (
                                <tr key={row.category_id}>
                                  <td className="code-cell">{row.code}</td>
                                  <td className="name-cell">{row.category_name}</td>
                                  <td className="amount-cell">
                                    {isEditMode && canManageFinance ? (
                                      <input
                                        type="text"
                                        value={row.debit_amount_input}
                                        onChange={(event) =>
                                          handleFieldChange(row.category_id, 'debit_amount_input', event.target.value)
                                        }
                                        placeholder="0"
                                      />
                                    ) : (
                                      formatMoney(ledgerValues.debitAmount)
                                    )}
                                  </td>
                                  <td className="amount-cell">
                                    {isEditMode && canManageFinance ? (
                                      <input
                                        type="text"
                                        value={row.credit_amount_input}
                                        onChange={(event) =>
                                          handleFieldChange(row.category_id, 'credit_amount_input', event.target.value)
                                        }
                                        placeholder="0"
                                      />
                                    ) : (
                                      formatMoney(ledgerValues.creditAmount)
                                    )}
                                  </td>
                                  <td className="amount-cell">{formatMoney(ledgerValues.debitBalance)}</td>
                                  <td className="amount-cell">{formatMoney(ledgerValues.creditBalance)}</td>
                                  <td className="note-cell">
                                    {isEditMode && canManageFinance ? (
                                      <input
                                        type="text"
                                        value={row.note_input}
                                        onChange={(event) => handleFieldChange(row.category_id, 'note_input', event.target.value)}
                                        placeholder="Açıklama"
                                      />
                                    ) : (
                                      <span className="note-value">{row.note_input || '-'}</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                            <tr className="group-summary-row">
                              <td colSpan={2}>Grup Toplamı</td>
                              <td className="amount-cell">{formatMoney(groupTotals.debitAmount)}</td>
                              <td className="amount-cell">{formatMoney(groupTotals.creditAmount)}</td>
                              <td className="amount-cell">{formatMoney(groupTotals.debitBalance)}</td>
                              <td className="amount-cell">{formatMoney(groupTotals.creditBalance)}</td>
                              <td className="note-cell">-</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      <div className="accounting-mobile-list">
                        {groupItems.map((row) => {
                          const ledgerValues = getRowLedgerValues(row);
                          return (
                            <article className="accounting-mobile-card" key={`mobile-${row.category_id}`}>
                              <div className="accounting-mobile-head">
                                <strong>{row.code}</strong>
                                <span>{row.category_name}</span>
                              </div>
                              <div className="accounting-mobile-grid">
                                <div>
                                  <label>Borç</label>
                                  {isEditMode && canManageFinance ? (
                                    <input
                                      type="text"
                                      value={row.debit_amount_input}
                                      onChange={(event) =>
                                        handleFieldChange(row.category_id, 'debit_amount_input', event.target.value)
                                      }
                                      placeholder="0"
                                    />
                                  ) : (
                                    <p>{formatMoney(ledgerValues.debitAmount)}</p>
                                  )}
                                </div>
                                <div>
                                  <label>Alacak</label>
                                  {isEditMode && canManageFinance ? (
                                    <input
                                      type="text"
                                      value={row.credit_amount_input}
                                      onChange={(event) =>
                                        handleFieldChange(row.category_id, 'credit_amount_input', event.target.value)
                                      }
                                      placeholder="0"
                                    />
                                  ) : (
                                    <p>{formatMoney(ledgerValues.creditAmount)}</p>
                                  )}
                                </div>
                                <div>
                                  <label>Borç Bakiyesi</label>
                                  <p>{formatMoney(ledgerValues.debitBalance)}</p>
                                </div>
                                <div>
                                  <label>Alacak Bakiyesi</label>
                                  <p>{formatMoney(ledgerValues.creditBalance)}</p>
                                </div>
                              </div>
                              <div className="accounting-mobile-note">
                                <label>Not</label>
                                {isEditMode && canManageFinance ? (
                                  <input
                                    type="text"
                                    value={row.note_input}
                                    onChange={(event) => handleFieldChange(row.category_id, 'note_input', event.target.value)}
                                    placeholder="Açıklama"
                                  />
                                ) : (
                                  <p>{row.note_input || '-'}</p>
                                )}
                              </div>
                            </article>
                          );
                        })}
                      </div>
                    </>
                  )}
                </section>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default Accounting;



